import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Palette, Layers } from 'lucide-react';
import type { ProductColor } from '@/types';
import { cn } from '@/lib/utils';

interface ProductColorConfiguratorProps {
    colors: ProductColor[];
    onColorSelect?: (color: ProductColor) => void;
    initialColor?: ProductColor;
    productImages?: { url: string; isPrimary?: boolean }[];
}

export const ProductColorConfigurator = ({
    colors,
    onColorSelect,
    initialColor,
    productImages,
}: ProductColorConfiguratorProps) => {
    const [selectedColor, setSelectedColor] = useState<ProductColor | null>(
        initialColor || colors[0] || null
    );
    const [previewImage, setPreviewImage] = useState<string | null>(
        selectedColor?.image || productImages?.[0]?.url || null
    );

    if (!colors || colors.length === 0) return null;

    const handleColorSelect = (color: ProductColor) => {
        setSelectedColor(color);
        setPreviewImage(color.image || productImages?.[0]?.url || null);
        onColorSelect?.(color);
    };

    // Group colors by fabricType if available
    const groupedColors = colors.reduce((acc, color) => {
        const group = color.fabricType || 'Standart';
        if (!acc[group]) acc[group] = [];
        acc[group].push(color);
        return acc;
    }, {} as Record<string, ProductColor[]>);

    const hasMultipleGroups = Object.keys(groupedColors).length > 1;

    return (
        <div className="space-y-6">
            {/* Preview Image with Color Overlay */}
            {previewImage && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="relative aspect-[4/3] rounded-xl overflow-hidden bg-stone-100"
                >
                    <AnimatePresence mode="wait">
                        <motion.img
                            key={previewImage}
                            src={previewImage}
                            alt={selectedColor?.name || 'Ürün'}
                            initial={{ opacity: 0, scale: 1.05 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.3 }}
                            className="w-full h-full object-cover"
                        />
                    </AnimatePresence>

                    {/* Selected Color Badge */}
                    {selectedColor && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-lg"
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    className="w-8 h-8 rounded-full border-2 border-white shadow-md"
                                    style={{ backgroundColor: selectedColor.hex }}
                                />
                                <div className="flex-1">
                                    <p className="font-medium text-charcoal text-sm">{selectedColor.name}</p>
                                    {selectedColor.fabricType && (
                                        <p className="text-xs text-gray-500">{selectedColor.fabricType}</p>
                                    )}
                                </div>
                                {selectedColor.materialCode && (
                                    <span className="text-xs text-gray-400 font-mono">
                                        {selectedColor.materialCode}
                                    </span>
                                )}
                            </div>
                        </motion.div>
                    )}
                </motion.div>
            )}

            {/* Color Selection */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-charcoal">
                    <Palette className="w-4 h-4" />
                    <span>Renk Seçenekleri</span>
                    <span className="text-gray-400 font-normal">({colors.length} renk)</span>
                </div>

                {hasMultipleGroups ? (
                    // Grouped by fabric type
                    <div className="space-y-4">
                        {Object.entries(groupedColors).map(([groupName, groupColors]) => (
                            <div key={groupName}>
                                <div className="flex items-center gap-2 mb-2">
                                    <Layers className="w-3 h-3 text-gray-400" />
                                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {groupName}
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {groupColors.map((color, index) => (
                                        <ColorSwatch
                                            key={`${groupName}-${index}`}
                                            color={color}
                                            isSelected={selectedColor?.name === color.name}
                                            onClick={() => handleColorSelect(color)}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    // Simple grid
                    <div className="flex flex-wrap gap-3">
                        {colors.map((color, index) => (
                            <ColorSwatch
                                key={index}
                                color={color}
                                isSelected={selectedColor?.name === color.name}
                                onClick={() => handleColorSelect(color)}
                                showName
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// Color Swatch Component
interface ColorSwatchProps {
    color: ProductColor;
    isSelected: boolean;
    onClick: () => void;
    showName?: boolean;
}

const ColorSwatch = ({ color, isSelected, onClick, showName = false }: ColorSwatchProps) => {
    const isLightColor = isLightHex(color.hex);

    return (
        <button
            onClick={onClick}
            className={cn(
                "group relative transition-all duration-200",
                showName ? "flex flex-col items-center gap-1.5" : ""
            )}
        >
            <div
                className={cn(
                    "w-10 h-10 rounded-full border-2 transition-all duration-200 flex items-center justify-center",
                    isSelected
                        ? "border-charcoal scale-110 shadow-lg"
                        : "border-gray-200 hover:border-gray-300 hover:scale-105"
                )}
                style={{ backgroundColor: color.hex }}
                title={color.name}
            >
                {isSelected && (
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className={cn(
                            "w-4 h-4 rounded-full flex items-center justify-center",
                            isLightColor ? "bg-black/20" : "bg-white/30"
                        )}
                    >
                        <Check className={cn("w-3 h-3", isLightColor ? "text-black" : "text-white")} />
                    </motion.div>
                )}
            </div>
            {showName && (
                <span
                    className={cn(
                        "text-[10px] max-w-[60px] truncate text-center transition-colors",
                        isSelected ? "text-charcoal font-medium" : "text-gray-500"
                    )}
                >
                    {color.name}
                </span>
            )}
        </button>
    );
};

// Helper to determine if a hex color is light
const isLightHex = (hex: string): boolean => {
    const cleanHex = hex.replace('#', '');
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6;
};
